import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexStroke,
  ApexYAxis,
  ApexFill,
  ApexTooltip,
  ApexMarkers,
  ApexGrid,
  ApexPlotOptions,
  ApexLegend,
  ApexStates
} from 'ng-apexcharts';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  dataLabels: ApexDataLabels;
  stroke: ApexStroke;
  fill: ApexFill;
  tooltip: ApexTooltip;
  markers: ApexMarkers;
  grid: ApexGrid;
  colors: string[];
  plotOptions: ApexPlotOptions;
  legend: ApexLegend;
  states: ApexStates;
};

import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LineChartComponent implements OnChanges {
  @Input() title: string = '';
  @Input() subtitle?: string;
  @Input() labels: string[] = [];
  @Input() datasets: { name: string; data: number[]; color?: string }[] = [];
  @Input() height: string = '350px';
  @Input() uniqueId: string = Math.random().toString(36).substring(7);
  @Input() period: 'Daily' | 'Weekly' | 'Monthly' | 'Yearly' = 'Yearly';

  public chartOptions: Partial<ChartOptions> = {};

  constructor(private translate: TranslateService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['labels'] || changes['datasets'] || changes['period']) {
      this.buildChart();
    }
  }

  private formatLabel(label: string): string {
    if (!label) return label;
    
    // API returns ISO strings like "2026-05-16T00:00:00Z"
    const date = new Date(label);
    if (isNaN(date.getTime())) return label;

    const locale = this.translate.currentLang || 'en';

    try {
      switch (this.period) {
        case 'Daily':
          // Show hour like "12 AM" using UTC to match API bucket
          return new Intl.DateTimeFormat(locale, { hour: 'numeric', hour12: true, timeZone: 'UTC' }).format(date);
        case 'Weekly':
          // Show day name like "Saturday"
          return new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(date);
        case 'Monthly':
          // Show day number like "1", "2"
          return new Intl.DateTimeFormat(locale, { day: 'numeric', timeZone: 'UTC' }).format(date);
        case 'Yearly':
          // Show month name like "January"
          return new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' }).format(date);
        default:
          return label;
      }
    } catch (e) {
      return label;
    }
  }

  private buildChart() {
    const palette = ['#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0', '#9966FF'];
    const formattedLabels = this.labels.map(l => this.formatLabel(l));
    
    this.chartOptions = {
      series: this.datasets.map(ds => ({
        name: ds.name,
        data: ds.data
      })),
      chart: {
        fontFamily: 'inherit',
        type: 'area',
        height: parseInt(this.height),
        toolbar: {
          show: false
        }
      },
      plotOptions: {},
      legend: {
        show: false
      },
      dataLabels: {
        enabled: false
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.3,
          opacityTo: 0.1,
          stops: [0, 90, 100]
        }
      },
      stroke: {
        curve: 'smooth',
        show: true,
        width: 3,
        colors: this.datasets.map((ds, i) => ds.color || palette[i % palette.length])
      },
      xaxis: {
        categories: formattedLabels,
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        },
        labels: {
          style: {
            colors: '#A1A5B7',
            fontSize: '12px'
          }
        },
        crosshairs: {
          position: 'front',
          stroke: {
            color: '#36A2EB',
            width: 1,
            dashArray: 3
          }
        },
        tooltip: {
          enabled: true,
          formatter: undefined,
          offsetY: 0,
          style: {
            fontSize: '12px'
          }
        }
      },
      yaxis: {
        labels: {
          style: {
            colors: '#A1A5B7',
            fontSize: '12px'
          }
        }
      },
      states: {
        normal: {
          filter: {
            type: 'none',
            value: 0
          }
        },
        hover: {
          filter: {
            type: 'none',
            value: 0
          }
        },
        active: {
          allowMultipleDataPointsSelection: false,
          filter: {
            type: 'none',
            value: 0
          }
        }
      },
      tooltip: {
        style: {
          fontSize: '12px'
        },
        y: {
          formatter: function (val: number) {
            return val.toString();
          }
        }
      },
      colors: this.datasets.map((ds, i) => ds.color || palette[i % palette.length]),
      grid: {
        borderColor: '#EFF2F5',
        strokeDashArray: 4,
        yaxis: {
          lines: {
            show: true
          }
        }
      },
      markers: {
        strokeColors: this.datasets.map((ds, i) => ds.color || palette[i % palette.length]),
        strokeWidth: 3
      }
    };
  }
}
